// assets/cart-discount.js
// Dependencies loaded globally: fetchConfig, debounce, dispatchCustomEvent, morphSection

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.isLoading = false;
    this.scrollPosition = 0; // Armazenar posição do scroll
    this._normalizingDiscounts = false; // flag para evitar loops de normalização
  }

  connectedCallback() {
    if (this.initialized) return;

    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount_code"]');
    this.errorElement = this.querySelector('.cart-discount__error');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.discountsList = this.querySelector('.cart-discount__codes');

    if (this.form) {
      this.form.addEventListener('submit', this.applyDiscount.bind(this));
    }

    this.setupRemoveButtons();

    // Carregar descontos existentes automaticamente
    this.checkAppliedDiscounts();

    // Garantir que o desconto seja levado ao checkout
    this.bindCheckoutWithDiscount();
    this.bindGlobalCheckoutIntercept();
    this.bindDiscountLinkIntercept();
    this.bindDiscountSyncOnVisibility();

    const accordion = this.querySelector('.cart-discount__accordion');
    if (accordion) {
      const contentEl =
        document.querySelector('#cart-drawer-root .cart-drawer__content') ||
        document.getElementById('cart-drawer-root');
      // O rodapé agora acompanha a rolagem dentro da sacola (cart-drawer-premium.css),
      // então não precisa mais de espaço extra no fim. Só limpa o que versões antigas deixaram.
      const applyPadding = () => {
        if (!contentEl) return;
        contentEl.style.removeProperty('padding-bottom');
      };
      applyPadding(accordion.hasAttribute('open'));
      accordion.addEventListener(
        'toggle',
        () => {
          const open = accordion.hasAttribute('open');
          window.__cartDiscountInteracting = open;
          applyPadding(open);
        },
        { passive: true }
      );
    }

    const inputEl = this.querySelector('.cart-discount__input');
    if (inputEl) {
      inputEl.addEventListener(
        'focus',
        () => {
          window.__cartDiscountInteracting = true;
        },
        { passive: true }
      );
      inputEl.addEventListener(
        'blur',
        () => {
          window.__cartDiscountInteracting = false;
        },
        { passive: true }
      );
      inputEl.addEventListener(
        'input',
        () => {
          this.updateGiftCardHint();
        },
        { passive: true }
      );
      inputEl.addEventListener('keydown', (e) => {
        if (
          e.key === 'Enter' &&
          this.looksLikeGiftCard(this.input?.value?.trim())
        ) {
          e.preventDefault();
          const hint = this.querySelector('.cart-discount__giftcard-hint');
          if (hint) hint.hidden = false;
        }
      });
      this.updateGiftCardHint();
    }

    this.initialized = true;
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.initialized = false;
  }

  // Método para salvar posição do scroll (adaptado para o seu drawer)
  saveScrollPosition() {
    const cartDrawer = document.getElementById('cart-drawer-root');
    if (cartDrawer) {
      const scrollContainer =
        cartDrawer.querySelector('.cart-drawer__content') || cartDrawer;
      this.scrollPosition = scrollContainer.scrollTop || 0;
    }
  }

  // Método para restaurar posição do scroll (adaptado para o seu drawer)
  restoreScrollPosition() {
    const cartDrawer = document.getElementById('cart-drawer-root');
    if (cartDrawer && this.scrollPosition > 0) {
      const scrollContainer =
        cartDrawer.querySelector('.cart-drawer__content') || cartDrawer;
      setTimeout(() => {
        scrollContainer.scrollTop = this.scrollPosition;
      }, 50);
    }
  }

  // Método otimizado para atualizar apenas elementos específicos
  updateCartElementsOnly(cartData) {
    try {
      // Salvar posição do scroll antes de qualquer atualização
      this.saveScrollPosition();

      // Check if Drawer is handling updates
      const drawerHandled =
        window.CART_DRAWER &&
        typeof window.CART_DRAWER.refreshDiscounts === 'function';

      // Se o drawer estiver presente e tiver a função de refresh otimizado, delegar a atualização
      if (drawerHandled) {
        window.CART_DRAWER.refreshDiscounts(cartData);
      }

      // Atualizar apenas preços totais
      const totalElements = document.querySelectorAll(
        '.totals__total-value, .cart__total-price, [data-cart-total]'
      );
      totalElements.forEach((element) => {
        if (cartData.total_price !== undefined) {
          element.textContent = this.formatMoney(cartData.total_price);
        }
      });

      if (!drawerHandled) {
        // Atualizar subtotal compare no drawer
        const compareEl = document.querySelector(
          '.cart-drawer__subtotal-compare'
        );
        if (compareEl) {
          const shouldShow =
            Number(cartData.original_total_price || 0) >
            Number(cartData.total_price || 0);
          if (shouldShow) {
            compareEl.textContent = this.formatMoney(
              cartData.original_total_price
            );
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }

        // Atualizar bloco de descontos globais do drawer
        const globalDiscounts = document.querySelector(
          '.cart-drawer__global-discounts'
        );
        if (globalDiscounts) {
          const containerParent = globalDiscounts.parentElement;
          if (
            Number(cartData.total_discount || 0) > 0 &&
            Array.isArray(cartData.discount_applications)
          ) {
            // Atualiza apenas se mudou
            const currentCount = globalDiscounts.querySelectorAll('.cart-drawer__global-discount').length;
            if (currentCount !== cartData.discount_applications.length) {
              // Remove filhos
              while (globalDiscounts.firstChild) {
                globalDiscounts.removeChild(globalDiscounts.firstChild);
              }
              const title = document.createElement('span');
              title.textContent = 'Descontos aplicados:';
              globalDiscounts.appendChild(title);
              cartData.discount_applications.forEach((d) => {
                const item = document.createElement('div');
                item.className = 'cart-drawer__global-discount';
                const t = document.createElement('span');
                t.className = 'cart-drawer__global-discount-title';
                t.textContent = d.title || '';
                const amt = document.createElement('span');
                amt.className = 'cart-drawer__global-discount-amount';
                if (d.total_allocated_amount != null) {
                  amt.textContent =
                    '-' + this.formatMoney(d.total_allocated_amount);
                }
                item.appendChild(t);
                item.appendChild(amt);
                globalDiscounts.appendChild(item);
              });
            } else {
              // Atualiza apenas valores
              const items = globalDiscounts.querySelectorAll('.cart-drawer__global-discount');
              items.forEach((item, idx) => {
                const d = cartData.discount_applications[idx];
                if (d) {
                  const t = item.querySelector('.cart-drawer__global-discount-title');
                  const amt = item.querySelector('.cart-drawer__global-discount-amount');
                  if (t && t.textContent !== d.title) t.textContent = d.title || '';
                  if (amt && d.total_allocated_amount != null) {
                    const newText = '-' + this.formatMoney(d.total_allocated_amount);
                    if (amt.textContent !== newText) amt.textContent = newText;
                  }
                }
              });
            }
            globalDiscounts.style.display = '';
          } else {
            if (containerParent && globalDiscounts) {
              containerParent.removeChild(globalDiscounts);
            }
          }
        }
      }

      // Atualizar contadores de itens
      const countElements = document.querySelectorAll(
        '.cart-count-bubble, [data-cart-count]'
      );
      countElements.forEach((element) => {
        if (cartData.item_count !== undefined) {
          element.textContent = cartData.item_count;
          element.setAttribute('data-count', cartData.item_count);
        }
      });

      if (!drawerHandled) {
        // Atualizar apenas a seção de descontos sem recriar o cart-drawer
        this.updateDiscountsOnly(cartData);

        // Atualizar preços por item e exibir descontos de linha
        this.updateLineItemPrices(cartData);
      }

      // Restaurar posição do scroll
      this.restoreScrollPosition();
    } catch (_) {
      // sem console (melhor performance / sem ruído)
    }
  }

  // Método para atualizar apenas a lista de descontos (normalizado: 1 <li> por código)
  updateDiscountsOnly(cartData) {
    if (this._normalizingDiscounts) return;
    this._normalizingDiscounts = true;

    const discountsList = this.querySelector(
      '.cart-discount__applied-discounts .discounts'
    );
    if (!discountsList) {
      this._normalizingDiscounts = false;
      return;
    }

    const code = (() => {
      if (
        Array.isArray(cartData.discount_codes) &&
        cartData.discount_codes.length
      ) {
        return cartData.discount_codes[cartData.discount_codes.length - 1]?.code;
      }
      const app = (cartData.cart_level_discount_applications || [])[0];
      return app?.title || '';
    })();

    const total = Number(cartData.total_discount || 0);

    // Só atualiza se mudou
    const existingCode = discountsList.querySelector('[data-discount-code]')?.dataset.discountCode;
    const existingAmount = discountsList.querySelector('.discount-amount')?.textContent;
    const newAmount = `(-${this.formatMoney(total)})`;
    
    if (existingCode === code && existingAmount === newAmount) {
      this._normalizingDiscounts = false;
      return;
    }

    // Remove todos os items
    while (discountsList.firstChild) {
      discountsList.removeChild(discountsList.firstChild);
    }

    if (code && total > 0) {
      const li = this.createDiscountListItem({ title: code, total_allocated_amount: total });
      discountsList.appendChild(li);
    }

    this.setupRemoveButtons();
    this._normalizingDiscounts = false;
  }

  // Atualiza preços e descontos por item no drawer
  updateLineItemPrices(cartData) {
    try {
      if (!cartData || !Array.isArray(cartData.items)) return;
      const itemsByKey = new Map();
      cartData.items.forEach((it) => itemsByKey.set(String(it.key), it));

      const itemEls = document.querySelectorAll('.cart-drawer__item');
      itemEls.forEach((el) => {
        const key = String(el.getAttribute('data-line-key') || '');
        if (!key || !itemsByKey.has(key)) return;
        const data = itemsByKey.get(key);

        const qtyEl = el.querySelector('[data-qty-value]');
        if (qtyEl) qtyEl.textContent = String(data.quantity);

        const finalEl = el.querySelector('.item__price--final');
        const compareEl = el.querySelector('.item__price--compare');

        if (finalEl) finalEl.textContent = this.formatMoney(data.final_price);

        if (compareEl) {
          if (data.original_price > data.final_price) {
            compareEl.textContent = this.formatMoney(data.original_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        const discountsList = el.querySelector('.item__discounts');
        if (discountsList) {
          const allocations = data.line_level_discount_allocations || [];
          const currentItems = discountsList.querySelectorAll('.item__discount');
          
          // Só atualiza se mudou
          if (currentItems.length !== allocations.length) {
            while (discountsList.firstChild) {
              discountsList.removeChild(discountsList.firstChild);
            }
            allocations.forEach((alloc) => {
              const li = document.createElement('li');
              li.className = 'item__discount';
              const title = document.createElement('span');
              title.className = 'item__discount-title';
              title.textContent = alloc?.discount_application?.title || 'Desconto';
              const amount = document.createElement('span');
              amount.className = 'item__discount-amount';
              amount.textContent = '-' + this.formatMoney(alloc.amount);
              li.appendChild(title);
              li.appendChild(amount);
              discountsList.appendChild(li);
            });
          } else {
            // Atualiza apenas valores
            currentItems.forEach((li, idx) => {
              const alloc = allocations[idx];
              if (alloc) {
                const title = li.querySelector('.item__discount-title');
                const amount = li.querySelector('.item__discount-amount');
                const newTitle = alloc?.discount_application?.title || 'Desconto';
                const newAmount = '-' + this.formatMoney(alloc.amount);
                if (title && title.textContent !== newTitle) title.textContent = newTitle;
                if (amount && amount.textContent !== newAmount) amount.textContent = newAmount;
              }
            });
          }
        }
      });
    } catch (_) {
      // sem console
    }
  }

  // Intercepta o checkout para anexar o cupom
  bindCheckoutWithDiscount() {
    try {
      if (!window.__cartCheckoutBound) {
        window.__cartCheckoutBound = true;
        document.addEventListener(
          'click',
          async (e) => {
            const btn =
              e.target &&
              e.target.closest &&
              e.target.closest('.cart-drawer__actions button[name="checkout"]');
            if (!btn) return;

            if (
              window.CART_GIFTS &&
              typeof window.CART_GIFTS.hasPendingGifts === 'function'
            ) {
              if (window.CART_GIFTS.hasPendingGifts()) return;
            }

            try {
              const res = await fetch('/cart.js', {
                headers: { Accept: 'application/json' },
              });
              const cart = res.ok ? await res.json() : null;
              let code = '';
              if (cart) {
                if (Array.isArray(cart.discount_codes) && cart.discount_codes.length) {
                  code = cart.discount_codes[cart.discount_codes.length - 1]?.code || '';
                } else if (cart.attributes && cart.attributes.discount_code) {
                  code = cart.attributes.discount_code || '';
                }
              }
              e.preventDefault();
              window.location.href = code
                ? '/checkout?discount=' + encodeURIComponent(code)
                : '/checkout';
            } catch (_) {
              e.preventDefault();
              window.location.href = '/checkout';
            }
          },
          true
        );
      }
    } catch (_) {}
  }

  // Intercepta qualquer botão/link com name="checkout" na página
  bindGlobalCheckoutIntercept() {
    try {
      if (!window.__globalCheckoutBound) {
        window.__globalCheckoutBound = true;
        document.addEventListener(
          'click',
          async (e) => {
            const btn = e.target && e.target.closest && e.target.closest('[name="checkout"]');
            if (!btn) return;

            if (
              window.CART_GIFTS &&
              typeof window.CART_GIFTS.hasPendingGifts === 'function'
            ) {
              if (window.CART_GIFTS.hasPendingGifts()) {
                if (btn.closest && btn.closest('.cart-drawer__actions')) {
                  return;
                }
              }
            }

            try {
              const res = await fetch('/cart.js', {
                headers: { Accept: 'application/json' },
              });
              const cart = res.ok ? await res.json() : null;
              let code = '';
              if (cart) {
                if (Array.isArray(cart.discount_codes) && cart.discount_codes.length) {
                  code = cart.discount_codes[cart.discount_codes.length - 1]?.code || '';
                } else if (cart.attributes && cart.attributes.discount_code) {
                  code = cart.attributes.discount_code || '';
                }
              }
              e.preventDefault();
              window.location.href = code
                ? '/checkout?discount=' + encodeURIComponent(code)
                : '/checkout';
            } catch (_) {
              e.preventDefault();
              window.location.href = '/checkout';
            }
          },
          true
        );
      }
    } catch (_) {}
  }

  // Intercepta links de cupom (ex.: /discount/CODE ou ?discount=CODE) e força checkout
  bindDiscountLinkIntercept() {
    try {
      if (!window.__discountLinkBound) {
        window.__discountLinkBound = true;
        document.addEventListener(
          'click',
          async (e) => {
            const a = e.target && e.target.closest && e.target.closest('a[href]');
            if (!a) return;
            const href = a.getAttribute('href') || '';
            const matchPath = href.match(/\/discount\/(\w[\w-]*)/i);
            const matchQuery = href.match(/[?&]discount=([^&#]+)/i);
            const code = matchPath?.[1] || matchQuery?.[1] || '';
            if (!code) return;

            e.preventDefault();
            try {
              await fetch('/discount/' + encodeURIComponent(code), {
                method: 'GET',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
              });
            } catch (_) {}
            window.location.href = '/checkout?discount=' + encodeURIComponent(code);
          },
          true
        );
      }
    } catch (_) {}
  }

  // Sincroniza desconto do checkout com o carrinho ao voltar/visibilidade
  bindDiscountSyncOnVisibility() {
    const sync = async () => {
      try {
        const res = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const cart = await res.json();

        if (!cart.discount_codes || cart.discount_codes.length === 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              discount_codes: [],
              attributes: { discount_code: '' },
            }),
          });
          this.clearDiscountCache();
        } else {
          const latest = cart.discount_codes[cart.discount_codes.length - 1]?.code || '';
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              attributes: { discount_code: latest },
            }),
          });
        }

        this.updateCartElementsOnly(cart);

        if (
          window.CART_DRAWER &&
          typeof window.CART_DRAWER.reload === 'function' &&
          !window.__cartDiscountInteracting
        ) {
          window.CART_DRAWER.reload();
        }
      } catch (_) {}
    };

    if (!window.__discountVisibilityBound) {
      window.__discountVisibilityBound = true;
      window.addEventListener('pageshow', sync);
    }
  }

  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.isLoading) return;

    const discountCode = this.input?.value?.trim();

    if (!discountCode) {
      this.#handleDiscountError('Por favor, digite um código de desconto');
      return;
    }

    // Gift card: apenas exibe aviso, não submete
    if (this.looksLikeGiftCard(discountCode)) {
      const hint = this.querySelector('.cart-discount__giftcard-hint');
      if (hint) hint.hidden = false;
      return;
    }

    // Check if discount is already applied
    if (this.#existingDiscounts().includes(discountCode)) {
      this.#handleDiscountError('Este cupom já foi aplicado');
      return;
    }

    this.saveScrollPosition();

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.isLoading = true;
    this.#setLoadingState(true);
    this.hideError();

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new URLSearchParams({
          discount: discountCode,
          'attributes[discount_code]': discountCode,
        }),
        signal: this.abortController.signal,
      });

      if (response.ok) {
        const cartData = await (
          await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        ).json();

        await this.updateCartElementsOnly(cartData);

        if (
          window.CART_DRAWER &&
          typeof window.CART_DRAWER.refreshDiscounts === 'function'
        ) {
          await window.CART_DRAWER.refreshDiscounts();
        }

        if (this.input) this.input.value = '';

        this.restoreScrollPosition();
      } else {
        this.showError('Código de desconto inválido');
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      this.#handleDiscountError('Cupom inválido ou expirado');
    } finally {
      this.isLoading = false;
      this.#setLoadingState(false);
      this.abortController = null;
    }
  };

  async forceCartDrawerRerender() {
    return;
  }

  async updateCartElementsOptimized() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();

        this.updateCartElementsOnly(cartData);
        this.setupRemoveButtons();

        if (this.isMobileDevice()) {
          this.ensureDiscountVisibilityMobile();
        }

        document.dispatchEvent(
          new CustomEvent('cart:discount-applied', {
            detail: { cartData, preserveScroll: true },
          })
        );
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { cart: cartData },
          })
        );
      }
    } catch (_) {
      // sem console
    }
  }

  isMobileDevice() {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768
    );
  }

  forceDiscountVisibility() {
    const discountsList = document.querySelector(
      '.cart-discount__applied-discounts .discounts'
    );
    if (discountsList) {
      discountsList.style.display = 'none';
      discountsList.offsetHeight; // Trigger reflow
      discountsList.style.display = '';

      const discountsContainer = discountsList.closest(
        '.cart-discount__applied-discounts'
      );
      if (discountsContainer) {
        discountsContainer.style.display = 'block';
      }
    }
  }

  ensureDiscountVisibilityMobile() {
    let attempts = 0;
    const maxAttempts = 5;

    const checkAndForceVisibility = () => {
      attempts++;

      this.forceDiscountVisibility();

      const discountsList = document.querySelector(
        '.cart-discount__applied-discounts .discounts'
      );
      const discountItems = document.querySelectorAll('.discounts__discount');

      if (discountsList && discountItems.length > 0) {
        const parent = discountsList.parentNode;
        const nextSibling = discountsList.nextSibling;

        parent.removeChild(discountsList);
        parent.offsetHeight;
        parent.insertBefore(discountsList, nextSibling);

        discountsList.style.display = 'block';
        discountsList.style.visibility = 'visible';
        discountsList.style.opacity = '1';

        const container = discountsList.closest(
          '.cart-discount__applied-discounts'
        );
        if (container) {
          container.style.display = 'block';
          container.style.visibility = 'visible';
        }
      }

      if (attempts < maxAttempts) {
        setTimeout(checkAndForceVisibility, 100 * attempts);
      }
    };

    checkAndForceVisibility();
    setTimeout(() => {
      this.forceDiscountVisibility();
    }, 500);
  }

  removeDiscount = async (event) => {
    event.preventDefault();
    const btn = event.target.closest('.cart-discount__remove-inline');
    const discountCode = btn?.dataset.discountCode;
    if (discountCode) {
      if (btn) {
        btn.setAttribute('aria-busy', 'true');
        btn.disabled = true;
        if (!btn.dataset.originalHtml) {
          btn.dataset.originalHtml = btn.innerHTML;
        }
        btn.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" stroke-width="2" stroke-opacity="0.25"/><path d="M7 1 A6 6 0 0 1 13 7" fill="none" stroke="currentColor" stroke-width="2"><animateTransform attributeName="transform" type="rotate" from="0 7 7" to="360 7 7" dur="0.6s" repeatCount="indefinite"/></path></svg>';
        const li = btn.closest('li');
        if (li) li.classList.add('removing');
      }
      await this.removeSpecificDiscount(discountCode).finally(() => {
        if (btn) {
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
          if (btn.dataset.originalHtml) {
            btn.innerHTML = btn.dataset.originalHtml;
          }
        }
      });
    }
  };

  removeSpecificDiscount = async (discountCode) => {
    // Evitar múltiplas chamadas simultâneas para o mesmo código
    if (this.isRemoving && this.isRemoving === discountCode) {
      return;
    }

    try {
      this.isRemoving = discountCode;

      this.saveScrollPosition();

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        signal: this.abortController.signal,
      });

      if (!cartResponse.ok) {
        throw new Error(`Failed to get cart: ${cartResponse.status}`);
      }

      // Mantém fetch para garantir fluxo idêntico (mesma lógica), mas sem logs
      await cartResponse.json();

      let ok = false;
      const updateResp = await fetch(
        window.Shopify.routes.root + 'cart/update.js',
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new URLSearchParams({
            discount: '',
            'attributes[discount_code]': '',
          }),
          signal: this.abortController.signal,
        }
      );

      ok = updateResp.ok;

      if (!ok) {
        await new Promise((r) => setTimeout(r, 250));
        const retryResp = await fetch(
          window.Shopify.routes.root + 'cart/update.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: new URLSearchParams({
              discount: '',
              'attributes[discount_code]': '',
            }),
            signal: this.abortController.signal,
          }
        );
        ok = retryResp.ok;
        if (!ok) {
          throw new Error(`HTTP error! status: ${retryResp.status}`);
        }
      }

      const data = await (
        await fetch('/cart.js', { headers: { Accept: 'application/json' } })
      ).json();

      this.clearDiscountCache();

      document.dispatchEvent(
        new CustomEvent('cart:discount-removed', {
          detail: { discountCode, cartData: data },
        })
      );
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: data },
        })
      );

      this.updateCartElementsOnly(data);
      this.updateDiscountsOnly(data);

      if (
        window.CART_DRAWER &&
        typeof window.CART_DRAWER.refreshDiscounts === 'function'
      ) {
        await window.CART_DRAWER.refreshDiscounts();
      }

      if (typeof window.dispatchCustomEvent === 'function') {
        window.dispatchCustomEvent('cart:discount-removed', {
          discountCode: discountCode,
          cart: data,
          preserveScroll: true,
        });
      }

      this.restoreScrollPosition();
    } catch (error) {
      if (error && error.name === 'AbortError') {
        return;
      }
      // Mantém feedback ao usuário (UX), mas sem console
      this.showError('Erro ao remover cupom de desconto');
      throw error;
    } finally {
      this.abortController = null;
      this.isRemoving = null;
    }
  };

  // Private methods
  #handleDiscountError(message) {
    this.showError(message);
  }

  #existingDiscounts() {
    const discountElements = this.querySelectorAll('[data-discount-code]');
    return Array.from(discountElements)
      .map((el) => el.dataset.discountCode)
      .filter(Boolean);
  }

  #setLoadingState(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', loading.toString());

      const loadingOverlay = this.submitButton.querySelector('.loading-overlay');
      const buttonText = this.submitButton.querySelector('.button-text');

      if (loadingOverlay && buttonText) {
        if (loading) {
          loadingOverlay.classList.remove('hidden');
          buttonText.style.opacity = '0';
        } else {
          loadingOverlay.classList.add('hidden');
          buttonText.style.opacity = '1';
        }
      }
    }

    if (this.input) {
      this.input.disabled = loading;
    }
  }

  createDiscountElement(discount) {
    const discountItem = document.createElement('li');
    discountItem.className = 'discounts__discount discounts__discount--cart-level';
    discountItem.setAttribute('data-discount-code', discount.title);

    discountItem.innerHTML = `
      <svg class="icon icon-discount" aria-hidden="true" focusable="false">
        <use href="#icon-discount"></use>
      </svg>
      <div class="discounts__discount-content">
        <span class="discounts__discount-title">${discount.title}</span>
        <span class="discounts__discount-value">-${this.formatMoney(
          discount.total_allocated_amount
        )}</span>
      </div>
      <button type="button" class="discounts__discount-remove button button--tertiary button--small" 
              data-discount-code="${discount.title}" aria-label="Remover cupom ${discount.title}">
        <svg class="icon icon-remove" aria-hidden="true" focusable="false">
          <use href="#icon-remove"></use>
        </svg>
      </button>
    `;

    return discountItem;
  }

  createDiscountForm(containerId = 'auto-discount-form', sectionId = 'auto-generated') {
    const container = document.getElementById(containerId);
    if (!container) {
      // sem console
      return null;
    }

    const discountFormHTML = `
      <cart-discount class="cart-discount" data-section-id="${sectionId}" id="cart-discount-${sectionId}">
        <div class="cart-discount__applied-discounts">
          <ul class="discounts list-unstyled" role="list" aria-label="Desconto" id="discounts-list-${sectionId}">
          </ul>
        </div>
        
        <details class="cart-discount__accordion" open="">
          <summary class="cart-discount__summary">
            <span class="cart-discount__title">Código de desconto</span>
            <span class="cart-discount__icon">
              <svg class="icon icon-caret" viewBox="0 0 10 6">
                <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>
              </svg>
            </span>
          </summary>
          
          <div class="cart-discount__content">
            <form class="cart-discount__form" action="/cart" method="post" novalidate="">
              <div class="cart-discount__field-wrapper">
                <div class="field">
                  <input class="field__input cart-discount__input" type="text" name="discount_code" id="CartDiscount-${sectionId}" placeholder="Seu código" autocomplete="off" autocapitalize="off" spellcheck="false">
                  <label class="field__label" for="CartDiscount-${sectionId}">Código de desconto</label>
                </div>
                <button type="submit" class="button cart-discount__button" name="add" aria-describedby="CartDiscount-${sectionId}-error">
                  <span class="button-text">Aplicar</span>
                  <div class="loading-overlay hidden">
                    <div class="loading-overlay__spinner">
                      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
              
              <div class="cart-discount__error hidden" id="CartDiscount-${sectionId}-error" role="alert" aria-live="polite">
                <svg class="icon icon-error" aria-hidden="true" focusable="false">
                  <use href="#icon-error"></use>
                </svg>
                <span class="cart-discount__error-text"></span>
              </div>
            </form>
          </div>
        </details>
      </cart-discount>
    `;

    container.innerHTML = discountFormHTML;

    if (!customElements.get('cart-discount')) {
      customElements.define('cart-discount', CartDiscount);
    }

    const cartDiscountElement = container.querySelector('cart-discount');
    this.loadExistingDiscounts(sectionId);

    return cartDiscountElement;
  }

  loadExistingDiscounts(sectionId) {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        if (
          cart.cart_level_discount_applications &&
          cart.cart_level_discount_applications.length > 0
        ) {
          this.renderDiscountsList(cart.cart_level_discount_applications, sectionId);
        }
      })
      .catch(() => {
        // sem console
      });
  }

  renderDiscountsList(discounts, sectionId) {
    const discountsList = document.getElementById(`discounts-list-${sectionId}`);
    if (!discountsList) return;

    discountsList.innerHTML = '';

    discounts.forEach((discount) => {
      const discountItem = this.createDiscountListItem(discount);
      discountsList.appendChild(discountItem);
    });
  }

  createDiscountListItem(discount) {
    const listItem = document.createElement('li');
    const discountType = discount.type || 'cart-level';
    listItem.className = `discounts__discount discounts__discount--${discountType}`;
    listItem.setAttribute('data-discount-code', discount.title);

    listItem.innerHTML = `
      <svg class="icon icon-discount" viewBox="0 0 12 12">
        <path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"></path>
      </svg>
      ${discount.title}
      <span class="discount-amount">(-${this.formatMoney(discount.total_allocated_amount)})</span>
      <button type="button" class="cart-discount__remove-inline" data-discount-code="${discount.title}" aria-label="Remover desconto ${discount.title}" title="Remover cupom">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </button>
    `;

    return listItem;
  }

  static generateDiscountForm(containerId, sectionId) {
    const instance = new CartDiscount();
    return instance.createDiscountForm(containerId, sectionId);
  }

  updateDiscountsList(sectionId) {
    this.loadExistingDiscounts(sectionId);
  }

  checkDiscountReturn() {
    const appliedDiscount = sessionStorage.getItem('applying_discount');
    if (appliedDiscount) {
      sessionStorage.removeItem('applying_discount');

      const input = this.querySelector('input[name="discount_code"]');
      if (input) input.value = '';

      setTimeout(() => {
        this.updateCartElementsOptimized();
      }, 500);
    }
  }

  clearDiscountCache() {
    const sessionKeys = [
      'applying_discount',
      'discount_code',
      'last_applied_discount',
      'cart_discount_state',
      'active_discount_code',
    ];
    sessionKeys.forEach((key) => {
      if (sessionStorage.getItem(key)) sessionStorage.removeItem(key);
    });

    const localKeys = [
      'discount_code',
      'applied_discounts',
      'cart_discounts',
      'active_discount_code',
    ];
    localKeys.forEach((key) => {
      if (localStorage.getItem(key)) localStorage.removeItem(key);
    });

    const input = this.querySelector('input[name="discount_code"]');
    if (input && input.value) input.value = '';
  }

  async clearAllDiscounts() {
    try {
      this.clearDiscountCache();

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          discount_codes: [],
          attributes: {
            discount_code: '',
            applied_discount: '',
          },
        }),
      });

      if (response.ok) {
        await this.updateCartElementsOptimized();
        if (window.CART_DRAWER && typeof window.CART_DRAWER.reload === 'function') {
          window.CART_DRAWER.reload();
        }

        const appliedSection = this.querySelector('.cart-discount__applied');
        if (appliedSection) appliedSection.remove();

        return true;
      }

      return false;
    } catch (_) {
      return false;
    }
  }

  setupRemoveButtons() {
    if (!this._boundRemoveDiscount) {
      this._boundRemoveDiscount = this.removeDiscount.bind(this);
    }

    const removeButtons = this.querySelectorAll(
      '.cart-discount__applied-discounts .cart-discount__remove-inline'
    );

    removeButtons.forEach((button) => {
      button.removeEventListener('click', this._boundRemoveDiscount);
      button.addEventListener('click', this._boundRemoveDiscount);
    });
  }

  checkAppliedDiscounts() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        this.cleanInvalidCodes(cartData);
        this.setupRemoveButtons();
      })
      .catch(() => {
        // sem console
      });
  }

  async cleanInvalidCodes(cartData) {
    if (!cartData.discount_codes || cartData.discount_codes.length === 0) return;

    const invalidCodes = [
      'CLEAR',
      'clear',
      'REMOVE',
      'remove',
      'DELETE',
      'delete',
      'TEST',
      'test',
      'DEMO',
      'demo',
      'SAMPLE',
      'sample',
      'EXPIRED',
      'expired',
      'INVALID',
      'invalid',
      'NULL',
      'null',
      'UNDEFINED',
      'undefined',
      'EMPTY',
      'empty',
    ];

    const hasInvalidCodes = cartData.discount_codes.some(
      (discount) =>
        invalidCodes.includes(discount.code.toUpperCase()) ||
        discount.applicable === false ||
        !discount.code ||
        discount.code.trim() === ''
    );

    const hasInvalidCartDiscounts =
      cartData.cart_level_discount_applications &&
      cartData.cart_level_discount_applications.some(
        (discount) =>
          !discount.title ||
          discount.total_allocated_amount === 0 ||
          discount.title.trim() === ''
      );

    if (hasInvalidCodes || hasInvalidCartDiscounts) {
      const validCodes = cartData.discount_codes.filter(
        (discount) =>
          discount.code &&
          discount.code.trim() !== '' &&
          !invalidCodes.includes(discount.code.toUpperCase()) &&
          discount.applicable !== false
      );

      try {
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount_codes: validCodes.map((discount) => discount.code),
            attributes: {
              discount_code:
                validCodes.length > 0 ? validCodes[validCodes.length - 1].code : '',
              applied_discount: validCodes.length > 0 ? 'true' : '',
            },
          }),
        });

        if (updateResponse.ok) {
          if (validCodes.length === 0) {
            await this.clearAllDiscounts();
          }
        }
      } catch (_) {
        // sem console
      }
    }
  }

  formatMoney(cents) {
    const value = cents / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  updateCartDrawer() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cartData) => {
        this.updateCartElementsOnly(cartData);
        this.updateDiscountsOnly(cartData);
      })
      .catch(() => {
        // sem console
      });
  }

  showError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
      this.errorElement.classList.remove('hidden');
    }
  }

  hideError() {
    if (this.errorElement) {
      this.errorElement.classList.add('hidden');
    }
  }

  looksLikeGiftCard(code) {
    if (!code) return false;
    const c = String(code).trim();
    const hyphen16 = /^[A-Za-z0-9]{4}(?:-[A-Za-z0-9]{4}){3}$/;
    const plain = /^[A-Za-z0-9]{12,20}$/;
    return hyphen16.test(c) || plain.test(c);
  }

  updateGiftCardHint() {
    const hint = this.querySelector('.cart-discount__giftcard-hint');
    if (!hint || !this.input) return;

    const show = this.looksLikeGiftCard(this.input.value || '');
    hint.hidden = !show;

    const btn = this.submitButton;
    if (btn) {
      btn.disabled = show;
      btn.setAttribute('aria-disabled', show ? 'true' : 'false');
      if (show) {
        btn.setAttribute('title', 'Cartão presente só pode ser resgatado no checkout');
      } else {
        btn.removeAttribute('title');
      }
    }
  }

  showLoading(show) {
    const button = this.querySelector('.cart-discount__button');
    const buttonText = button?.querySelector('.button-text');
    const loadingSpinner = button?.querySelector('.loading-spinner');

    if (buttonText && loadingSpinner) {
      if (show) {
        buttonText.style.display = 'none';
        loadingSpinner.style.display = 'inline-block';
        button.disabled = true;
      } else {
        buttonText.style.display = 'inline';
        loadingSpinner.style.display = 'none';
        button.disabled = false;
      }
    }
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}

// Delegação global para garantir funcionamento do botão de remover
try {
  if (!window.__globalDiscountRemoveBound) {
    window.__globalDiscountRemoveBound = true;
    document.addEventListener(
      'click',
      function (event) {
        const removeButton =
          event.target &&
          event.target.closest &&
          event.target.closest('.cart-discount__remove-inline');
        if (!removeButton) return;

        event.preventDefault();
        event.stopPropagation();

        const code = removeButton.getAttribute('data-discount-code') || '';

        let el = removeButton.closest('cart-discount');

        if (!el) {
          el =
            document.querySelector('#cart-drawer-root cart-discount') ||
            document.querySelector('cart-discount');
        }

        if (el && typeof el.removeSpecificDiscount === 'function') {
          el.removeSpecificDiscount(code);
        }
      },
      true
    );
  }
} catch (_) {}

// Limpar cookies relacionados a desconto
const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch (_) {}
};
['discount_code', 'active_discount_code', 'applied_discounts', 'cart_discounts'].forEach(
  deleteCookie
);
